import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ExternalLink, Download, BookOpen, Loader2, Menu } from 'lucide-react';
import { useHelp } from '../contexts/HelpContext';
import { findHelpSection } from '../config/helpSections';

// Lazy-load the PDF body so react-pdf + pdfjs-dist (~70 KB minzipped
// + a 1 MB worker) only get fetched when the drawer is first opened.
// The chrome (header, animation, banner suggestion) is fast and stays
// in the main bundle.
const HelpDrawerBody = lazy(() => import('./HelpDrawerBody'));

/**
 * Right-edge slide-over that renders the printed user guide PDF inline
 * via PDF.js (lazy-loaded).
 *
 * iOS Safari treats PDF iframes as a single non-scrollable element on
 * iPhone, so we render via PDF.js into our own scroll container —
 * native momentum scroll, pinch-zoom, and copyable text on every
 * device.
 *
 * The PDF lives at /TWB_User_Guide.pdf (served from /public). To
 * publish a new revision, drop the file there and redeploy — no code
 * changes needed for content updates. The file carries its own embedded
 * outline (bookmarks); the body reads it via pdf.getOutline() to build
 * the Sections sidebar.
 *
 * Contextual help: when the drawer opens, the user's current route is
 * mapped (via src/config/helpSections.ts) to a section keyword, which
 * we search for in the PDF text. If found, a "Jump to: {section} →"
 * banner appears at the top of the body. The banner is dismissible
 * and never auto-jumps — we'd rather a wrong guess be silently
 * ignored than yank someone to the wrong page.
 */

const PDF_PATH = '/TWB_User_Guide.pdf';

const HelpDrawer: React.FC = () => {
  const { isOpen, close } = useHelp();
  const location = useLocation();
  const [numPages, setNumPages] = useState<number>(0);
  const [hasOutline, setHasOutline] = useState(false);
  // Single source of truth for sidebar visibility across all viewports.
  // Initialized to viewport-appropriate default (open on desktop where
  // there's room, closed on mobile to keep first-open clean), then the
  // user controls via the toggle button. State is preserved across
  // drawer open/close cycles within a session — last toggled state
  // survives until the page is refreshed.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  const suggestedSection = findHelpSection(location.pathname);

  // Lock body scroll while the drawer is open and wire up Escape-to-close.
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  return (
    <>
      <div
        onClick={close}
        aria-hidden="true"
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <aside
        role="dialog"
        aria-label="User Guide"
        aria-hidden={!isOpen}
        className={`fixed top-0 right-0 h-full w-full sm:w-[90%] md:w-[70%] lg:w-1/2 xl:w-[640px] z-50 bg-white dark:bg-dark-800 shadow-2xl flex flex-col transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* `pt-safe` keeps the Close X / Open-in-new-tab / Download
            buttons below the iOS status bar when launched as a
            home-screen PWA. The drawer is fixed top-0 / h-full,
            so without this padding the row of action buttons can
            sit under the iOS clock / battery and be tappable but
            invisible. Belt-and-suspenders for users who installed
            the PWA before status-bar-style switched to "default". */}
        <header className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-dark-700 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <div className="flex items-center gap-2 min-w-0">
            {hasOutline ? (
              <button
                type="button"
                onClick={() => setSidebarOpen((v) => !v)}
                className="p-1.5 -ml-1 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors flex-shrink-0"
                title={sidebarOpen ? 'Hide sections' : 'Show sections'}
                aria-label={sidebarOpen ? 'Hide sections' : 'Show sections'}
                aria-expanded={sidebarOpen}
              >
                <Menu className="w-5 h-5" />
              </button>
            ) : null}
            <BookOpen className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <h2 className="font-semibold text-gray-800 dark:text-white truncate">
              User Guide
            </h2>
            {numPages > 0 ? (
              <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 hidden sm:inline">
                · {numPages} page{numPages === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={PDF_PATH}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in new tab"
              aria-label="Open in new tab"
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
            <a
              href={PDF_PATH}
              download
              title="Download PDF"
              aria-label="Download PDF"
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            >
              <Download className="w-5 h-5" />
            </a>
            <button
              type="button"
              onClick={close}
              title="Close (Esc)"
              aria-label="Close help drawer"
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>
        {isOpen ? (
          <Suspense
            fallback={
              <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                <span className="text-sm">Loading viewer…</span>
              </div>
            }
          >
            <HelpDrawerBody
              pdfPath={PDF_PATH}
              suggestedSection={suggestedSection}
              onPagesLoaded={setNumPages}
              sidebarOpen={sidebarOpen}
              onSidebarChange={setSidebarOpen}
              onOutlineLoaded={setHasOutline}
            />
          </Suspense>
        ) : null}
      </aside>
    </>
  );
};

export default HelpDrawer;
